import React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { PublicSessionLoginSuccess } from 'src/assets/public-session-login-success';
import { CountdownModal } from 'src/components/modals/countdown-modal';
import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useCreatePublicContextSessionMutation } from 'types/graphql';
import { removePersistedValue } from 'shared/utils/persisted-values';
import { KIOSK_PRO_MEDIA } from '../../kiosk-pro-shared/kiosk-pro-shared.styles';

export function PhoneNumberLoginSuccess({
  userFirstName,
  kioskMenuUrl,
  className,
  onClose,
  onResetLoginState,
}: {
  userFirstName: string;
  kioskMenuUrl: string;
  className?: string;
  onClose?: () => void;
  onResetLoginState?: () => void;
}): JSX.Element {
  const router = useRouter();
  const User = useUser();
  const { dispensary } = useDispensary();
  const [createPublicContextSessionMutation] = useCreatePublicContextSessionMutation();
  const cName = String(dispensary?.cName ?? '');
  const dispensaryId = dispensary?.id;

  /**
   * Handles the go back action by logging out the user, resetting login state,
   * creating a new session, and navigating to the public session page with the new session ID.
   */
  const handleGoBack = async (): Promise<void> => {
    await User?.logout();
    removePersistedValue('access-token');
    onResetLoginState?.();

    if (!dispensaryId) {
      if (onClose) {
        onClose();
        return;
      }
      void router.back();
      return;
    }

    const result = await createPublicContextSessionMutation({
      variables: {
        dispensaryId,
      },
    });

    const newSessionId = result.data?.createPublicContextSession?.id;

    if (!newSessionId) {
      if (onClose) {
        onClose();
        return;
      }
      void router.back();
      return;
    }

    // For modal context: update session-id in URL and stay on the same page
    if (onClose) {
      void router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, 'session-id': newSessionId },
        },
        undefined,
        { shallow: true }
      );
      return;
    }

    // For page context: navigate to the phone login page
    void router.push(`/kiosks/${cName}/login/public-session-phone-continue?session-id=${newSessionId}`);
  };

  return (
    <PhoneNumberLoginSuccessContainer className={className}>
      <SuccessIconContainer>
        <PublicSessionLoginSuccess />
      </SuccessIconContainer>
      <CountdownModal
        startNewSession={() => void router.push(kioskMenuUrl)}
        closeAndContinue={() => void handleGoBack()}
        timeLeftInSeconds={5}
        title={
          <>
            Shopping as
            <br />
            <b>{userFirstName}</b>
          </>
        }
        subtitle=''
        primaryButtonText='Looks good'
        secondaryButtonText='Go back'
      />
    </PhoneNumberLoginSuccessContainer>
  );
}

const PhoneNumberLoginSuccessContainer = styled.div`
  padding: 72px;
  text-align: center;
  width: 100%;
  max-width: 600px;

  h3 {
    font-size: 72px;
    margin-bottom: 72px;
    font-weight: 500;

    b {
      font-weight: 700;
    }

    @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
      font-size: 56px;
      margin-bottom: 56px;
    }
  }

  button {
    max-width: 420px;
    font-size: 36px;
    padding: 36px 56px;
    height: auto;
    line-height: 1;

    @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
      font-size: 28px;
      padding: 28px 40px;
    }
  }

  [data-testid='idle-warning-new-session'] {
    order: 1;
    margin-bottom: 28px;
  }

  [data-testid='idle-warning-continue-shopping'] {
    order: 2;
  }
`;

export const SuccessIconContainer = styled.div`
  margin-bottom: 72px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    margin-bottom: 56px;
  }
`;
